/**
 * VendorGridOverlay, full-screen "see all" Pinterest-style grid.
 *
 * When the user taps "See all {N}" on a recommendation set, this
 * overlay takes over the scroll region (mobile: full-screen modal;
 * desktop: slide-out panel from the right). The chat thread is
 * preserved behind it; "Back to chat" closes the overlay.
 *
 * Pioneer alignment:
 *  - Krug: clear "Close" affordance; cards work the same way as the
 *    in-chat carousel cards.
 *  - Pablo Stanley: focus trap so screen-reader users can't escape
 *    the overlay accidentally; Esc to close.
 */

import { X } from "lucide-react";
import { useEffect, useRef } from "react";
import { SkuCard, SkuCardData } from "./SkuCard";

interface VendorGridOverlayProps {
  /** Title summarising the filter set, e.g. "Sarah · pottery · ~$80". */
  title: string;
  /** All matching SKUs. */
  skus: SkuCardData[];
  /** Close callback. */
  onClose: () => void;
  /** MS61a: was `onSend`. Cards in the overlay now go through the
   *  same View → ProductDetailModal flow as the chat carousel —
   *  the parent typically closes the overlay first, then opens
   *  the detail modal. */
  onView: (sku: SkuCardData) => void;
}

export function VendorGridOverlay({
  title,
  skus,
  onClose,
  onView,
}: VendorGridOverlayProps) {
  const closeRef = useRef<HTMLButtonElement>(null);

  // Focus the close button on open + handle Escape.
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Lock body scroll while overlay is open.
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`All options for ${title}`}
      className="fixed inset-0 z-50 flex flex-col bg-white"
    >
      {/* Header */}
      <header
        className="flex items-center gap-3 border-b border-charcoal/10 bg-white px-4 py-3"
        style={{ fontFamily: "DM Sans, sans-serif" }}
      >
        <h2 className="flex-1 truncate text-base font-semibold text-navy">{title}</h2>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label="Close all options"
          /* MS203: was h-9 w-9 (36px). Below the 44px tap-target
             floor. Same fix as MS201's back button — header row
             (px-4 py-3) has the vertical room; only the hit area
             grows, the X icon stays at 18px. */
          className="inline-flex h-11 w-11 items-center justify-center rounded-full text-charcoal/70 hover:bg-charcoal/8 hover:text-navy focus:outline-none focus:ring-2 focus:ring-coral"
        >
          <X size={18} strokeWidth={2.5} />
        </button>
      </header>

      {/* Grid */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {skus.map((sku) => (
            <SkuCard key={sku.sku_id} sku={sku} variant="grid" onView={onView} />
          ))}
        </div>
        {skus.length === 0 && (
          <p
            className="mx-auto max-w-md py-12 text-center text-sm text-charcoal/60"
            style={{ fontFamily: "DM Sans, sans-serif" }}
          >
            Nothing matches the filters yet. Try a different vibe or budget.
          </p>
        )}
      </div>

      {/* Footer with "Back to chat" */}
      <div className="border-t border-charcoal/10 bg-white px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          className="w-full rounded-full border-2 border-navy/15 bg-white px-4 py-2.5 text-sm font-semibold text-navy hover:border-coral hover:text-coral focus:outline-none focus:ring-2 focus:ring-coral"
          style={{ fontFamily: "DM Sans, sans-serif" }}
        >
          Back to chat
        </button>
      </div>
    </div>
  );
}
